/*******************************************************
 * Copyright (C) BoostCommerce.net
 *
 * This file is part of commercial BoostCommerce.net projects.
 *
 * This file can not be copied and/or distributed without the express
 * permission of BoostCommerce.net
 *
 * @Date:   2019-08-16 22:11:48
 *
 *******************************************************/
/// @flow

import _ from 'lodash';
import React, { Component } from 'react';
import { connect } from 'react-redux';
import { Field, change, getFormValues } from 'redux-form';
import { ActionList, Badge, Button, FormLayout, Icon, Link, Popover } from '@shopify/polaris';
import { withI18n, WithI18nProps } from '@shopify/react-i18n';
import { ExternalSmallMinor, SelectMinor } from '@shopify/polaris-icons';

import { RenderSelect } from 'src/components/views/common/forms/render-select';
import RenderAutocompleteV2 from 'src/components/views/common/autocomplete-hoc-v2';
import { RenderLabelTooltip } from 'src/components/views/common/forms/render-label-tooltip';
import {
  FILTER_OPTION_DISPLAY_TYPES_DEFAULT,
  FILTER_OPTION_DISPLAY_TYPES_OPTION_TYPE,
  FILTER_OPTION_SELECT_TYPES,
  FILTER_OPTION_SWATCH_STYLES,
  getFilterOptionValueTypes
} from 'src/constants/filter-tree';
import { APP_LANGUAGE_ENGLISH_VALUE } from 'src/constants/language-setting';
import { routes } from 'src/routes';
import { getShop } from 'src/helpers';
import { URL } from 'src/constants/links';
import history from 'src/routes/history';
import CrownPremium from 'src/assets/images/common/crown-red.png';

import * as FilterDataActions from 'actions/filter-data-actions';

type Props = {
  type: string,
  optionType: String,
  optionsList: Array<Object>,
  valueType: string,
  displayType: string,
  themeSetupV2: boolean,
  host: string,
  appLanguage: string
};

type ComposedProps = Props & WithI18nProps;

class OptionType extends Component<ComposedProps> {
  state = {
    popoverSelectActive: false
  };

  componentDidMount() {
    this.getFilterData(this.props);
  }

  componentWillReceiveProps(nextProps) {
    // Change option type
    if (this.props.optionType !== nextProps.optionType) {
      // Get new filter option value
      this.getFilterData(nextProps);
    }
  }

  getFilterData = props => {
    const { optionType, getFilterDataByType } = props;
    const prefix = this.getFilterPrefix(optionType);

    getFilterDataByType('options', {
      skip: 0,
      limit: 100,
      keyword: '',
      prefix: prefix ? encodeURIComponent(prefix) : ''
    });
  };

  render() {
    const {
      host,
      i18n,
      updateField,
      enabledSwatchFeature,
      optionType,
      valueType,
      displayType,
      manualValues,
      themeSetupV2,
      appLanguage
    } = this.props;

    let displayTypes = FILTER_OPTION_DISPLAY_TYPES_OPTION_TYPE.map(function (type) {
      return {
        ...type,
        label: i18n.translate(type.label)
      };
    });
    if (!enabledSwatchFeature) {
      displayTypes = FILTER_OPTION_DISPLAY_TYPES_DEFAULT.map(function (type) {
        return {
          ...type,
          label: i18n.translate(type.label)
        };
      });
      displayTypes.push({
        label: (
          <p className="option-premium-lock">
            <span>{i18n.translate('AdminHTML.FilterTree.Common.swatch')}</span>
            <Badge status="warning">
              <img alt="crown" src={CrownPremium} width={11} height={8} />
              <span className="title">{i18n.translate('AdminHTML.Common.Badge.premium')}</span>
            </Badge>
            <span>
              <Link>
                {i18n.translate('AdminHTML.Common.Action.upgradeToUnlock')}
                <Icon source={ExternalSmallMinor} color={'interactive'}></Icon>
              </Link>
            </span>
          </p>
        ),
        value: '',
        action: () => history.push(routes.subscription.package())
      });
    }
    const selectTypes = FILTER_OPTION_SELECT_TYPES.map(function (type) {
      return {
        ...type,
        label: i18n.translate(type.label)
      };
    });
    const swatchStyles = FILTER_OPTION_SWATCH_STYLES.map(function (style) {
      return {
        ...style,
        label: i18n.translate(style.label)
      };
    });

    const optionOriginalName = this.getOptionOriginalName(optionType);
    const valueTypes = getFilterOptionValueTypes(optionOriginalName, appLanguage, i18n);
    const shop = getShop();

    return (
      <FormLayout>
        <Popover
          active={this.state.popoverSelectActive}
          fullWidth
          activator={
            <div className="analytics-select-popover">
              <p>
                <RenderLabelTooltip
                  label={i18n.translate(
                    'AdminHTML.FilterTree.Item.optionModal_GeneralTab_Field_OptionDisplay_Label'
                  )}
                  tooltip={i18n.translate(
                    'AdminHTML.FilterTree.Item.optionModal_GeneralTab_Field_OptionDisplay_Tooltip'
                  )}
                />
              </p>
              <Button onClick={() => this.setState({ popoverSelectActive: true })}>
                <p>{displayTypes.find(e => e.value === displayType)?.label}</p>
                <Icon source={SelectMinor} />
              </Button>
            </div>
          }
          onClose={() => this.setState({ popoverSelectActive: false })}>
          <ActionList
            items={displayTypes.map(type => ({
              content: type.label,
              disabled: type.disabled,
              onAction: type.action
                ? type.action
                : () => {
                    this.setState({
                      popoverSelectActive: false
                    });
                    updateField('opt_DisplayType', type.value);
                  }
            }))}
          />
        </Popover>
        {displayType === 'swatch' &&
          (themeSetupV2 ? (
            <span>
              <span style={{ color: '#DE3618', fontWeight: 500 }}>
                {i18n.translate(
                  'AdminHTML.FilterTree.Item.optionModal_GeneralTab_HelperText_ImportantNOTE'
                )}
              </span>{' '}
              {i18n.translate(
                'AdminHTML.FilterTree.Item.optionModal_GeneralTab_HelperText1_Content',
                {
                  t: (
                    <Link
                      external
                      url={`${routes.swatchSettings.view()}?shop=${shop}&host=${host}`}>
                      {i18n.translate(
                        'AdminHTML.FilterTree.Item.optionModal_GeneralTab_HelperText1_Content_t'
                      )}
                    </Link>
                  )
                }
              )}
            </span>
          ) : (
            <span>
              <span style={{ color: '#DE3618', fontWeight: 500 }}>
                {i18n.translate(
                  'AdminHTML.FilterTree.Item.optionModal_GeneralTab_HelperText_ImportantNOTE'
                )}
              </span>{' '}
              {i18n.translate(
                'AdminHTML.FilterTree.Item.optionModal_GeneralTab_HelperText2_Content',
                {
                  t: (
                    <Link external url={URL.OptionTypeSwatchDoc}>
                      {i18n.translate(
                        'AdminHTML.FilterTree.Item.optionModal_GeneralTab_HelperText2_Content_t'
                      )}
                    </Link>
                  )
                }
              )}
            </span>
          ))}
        {displayType === 'swatch' && themeSetupV2 && (
          <Field
            name="opt_SwatchStyle"
            component={RenderSelect}
            label={i18n.translate(
              'AdminHTML.FilterTree.Item.optionModal_GeneralTab_Field_SwatchStyle_Label'
            )}
            options={swatchStyles}
          />
        )}

        <Field
          name="opt_SelectType"
          component={RenderSelect}
          label={i18n.translate(
            'AdminHTML.FilterTree.Item.optionModal_GeneralTab_Field_OptionSelect_Label'
          )}
          options={selectTypes}
          tooltip={i18n.translate(
            'AdminHTML.FilterTree.Item.optionModal_GeneralTab_Field_OptionSelect_Tooltip'
          )}
        />

        <Field
          name="opt_ValueType"
          component={RenderSelect}
          label={i18n.translate(
            'AdminHTML.FilterTree.Item.optionModal_GeneralTab_Field_OptionValues_Label'
          )}
          options={valueTypes}
          tooltip={i18n.translate(
            'AdminHTML.FilterTree.Item.optionModal_GeneralTab_Field_OptionValues_Tooltip'
          )}
        />

        {valueType === 'specific' ? (
          <Field
            name="opt_ManualValues"
            component={RenderAutocompleteV2}
            label={i18n.translate(
              'AdminHTML.FilterTree.Item.optionModal_GeneralTab_Field_ManualValues_Label'
            )}
            value={manualValues}
            type={'options'}
            filterPrefix={this.getFilterPrefix(optionType)}
            placeholder={i18n.translate(
              'AdminHTML.FilterTree.Item.optionModal_GeneralTab_OptionTypes_Placeholder',
              {
                nt_item: optionOriginalName
              }
            )}
          />
        ) : (
          ''
          /*<Field
            name="opt_ExcludedValues"
            component={RenderAutocomplete}
            label={i18n.translate(
              'AdminHTML.FilterTree.Item.optionModal_GeneralTab_Field_ExcludedValues_Label'
            )}
            options={optionsList}
            tooltip={i18n.translate(
              'AdminHTML.FilterTree.Item.optionModal_GeneralTab_OptionTypes_Field5_Tooltip',
              {
                nt_type_of_value: type.replace(/_+/g, ' ')
              }
            )`Enter the ${type.replace(
              /_+/g,
              ' '
            )} values that need to be made exceptions and not shown in the filter option`}
            placeholder={i18n.translate(
              'AdminHTML.FilterTree.Item.optionModal_GeneralTab_OptionTypes_Placeholder',
              {
                nt_item: pluralize(type.replace(/_+/g, ' '))
              }
            )}
          />*/
        )}
      </FormLayout>
    );
  }

  getOptionOriginalName = optionType => {
    const { optionsList } = this.props;

    const optionName = optionType ? optionType.replace('opt_', '') : optionType;
    const option = _.find(optionsList, { name: optionName }) || {};
    const optionOriginalName = _.get(option, 'originalName', optionName);

    return optionOriginalName;
  };

  getFilterPrefix = optionType => {
    const optionName = optionType ? optionType.replace('opt_', '') : optionType;
    const filterPrefix = `${optionName}:`;

    return filterPrefix;
  };
}

const mapDispatchToProps = (dispatch, ownProps) => ({
  updateField: (field, value) => dispatch(change(`OptionModalForm`, field, value)),
  getFilterDataByType: (type, options) =>
    dispatch(FilterDataActions.getFilterDataByType(type, options, ownProps.i18n))
});

const mapStateToProps = (state, props) => {
  const formFilterOptionValues = getFormValues('OptionModalForm')(state);
  const optionType = _.get(formFilterOptionValues, 'optionType', '');
  const type = optionType ? optionType.substring(4) : '';
  const options = _.get(state.filterData.filterData, type, []);
  const optionsList = _.get(state.filterData.filterData, 'optionsList', []);
  const tenantApp = _.get(state.oauth, 'tenantApp', {});

  return {
    type,
    optionType,
    options,
    optionsList,
    enabledSwatchFeature: _.get(tenantApp, 'settings.productFilter.settings.swatch-filter', true),
    valueType: _.get(formFilterOptionValues, 'opt_ValueType', ''),
    displayType: _.get(formFilterOptionValues, 'opt_DisplayType', ''),
    manualValues: _.get(formFilterOptionValues, 'opt_ManualValues', []),
    themeSetupV2: _.get(tenantApp, 'adminAppFeatures.themeSetupV2', false),
    host: _.get(state.oauth, 'host', ''),
    appLanguage: _.get(state.oauth.tenantApp, 'appLanguage', APP_LANGUAGE_ENGLISH_VALUE)
  };
};

export default connect(mapStateToProps, mapDispatchToProps)(withI18n()(OptionType));
